import React from 'react';
import styled from 'styled-components';

import { colors, media } from '../../../core';

type colorsKeys = keyof typeof colors;

export interface ButtonProps {
  as?: any;
  backGroundColor?: (typeof colors)[colorsKeys];
  href?: string;
  size: 'xsmall' | 'small' | 'smallmedium' | 'medium' | 'large';
  variant: 'primary' | 'secondary' | 'outline-reverse';
  width?: string;
  children?: React.ReactNode;
}

const Button = styled.button.attrs(({ type }) => ({
  type: type || 'button',
}))<ButtonProps>`
  -webkit-appearance: none;
  border: 0;
  cursor: default;
  display: inline-flex;
  font-family: ${({ theme }) => theme.buttonFont || 'Quicksand, sans-serif'};
  font-size: 16px;
  font-weight: ${({ theme }) => theme.buttonFontWeight || 600};
  line-height: 20px;
  padding: 0 12px;
  text-decoration: none;
  text-align: center;
  align-items: center;
  justify-content: center;

  &:hover {
    cursor: pointer;
  }

  &:focus {
    outline: 0;
    box-shadow: ${({ theme }) => theme.buttonFocusShadow || `0 0 3px ${colors.primaryHover}`};
  }

  &:disabled {
    cursor: default;
  }

  ${({ variant, theme }) =>
    variant === 'primary' &&
    `
    background-color: ${theme.buttonPrimaryColor || colors.primary};
    color: ${theme.buttonPrimaryTextColor || colors.white};
    transition: background-color 0.4s ease, color 0.4s ease;

    &:hover,
    &:focus {
      background-color: ${theme.buttonPrimaryHoverColor || colors.primaryHover};
      color: ${theme.buttonPrimaryHoverTextColor || colors.white};
    }

    &:disabled {
      background-color: ${theme.buttonPrimaryDisabledColor || colors.primaryDisabled};
      color: ${theme.buttonPrimaryDisabledTextColor || colors.white};
    }
  `}

  ${({ variant, theme, backGroundColor }) =>
    variant === 'secondary' &&
    `
    border: 1px solid ${backGroundColor || theme.buttonSecondaryBorderColor || colors.primary};
    color: ${backGroundColor || theme.buttonSecondaryColor || colors.primary};
    transition: background-color 0.4s ease, color 0.4s ease;
    background-color: ${theme.buttonSecondaryBackgroundColor || 'transparent'};

    &:hover,
    &:focus {
      border-color: ${
        backGroundColor || theme.buttonSecondaryHoverBorderColor || colors.primaryHover
      };
      color: ${backGroundColor || theme.buttonSecondaryHoverColor || colors.primaryHover};
    }

    &:disabled {
      border-color: ${theme.buttonSecondaryDisabledColor || colors.primaryDisabled};
      color: ${theme.buttonSecondaryDisabledColor || colors.primaryDisabled};
    }
  `}

  ${({ variant }) =>
    variant === 'outline-reverse' &&
    `
    background-color: transparent;
    border: 1px solid ${colors.white};
    color: ${colors.white};
    transition: background-color 0.4s ease, color 0.4s ease, opacity: 0.4s ease;

    &:hover,
    &:focus {
      opacity: 0.9;
      border-color: ${colors.white};
      color: ${colors.white};
    }

    &:disabled {
      opacity: 0.5;
    }
  `}

  ${({ size, theme }) =>
    size === 'xsmall' &&
    `
      font-size: ${theme.buttonFontSize.xsmall};
      line-height: 18px;
      min-width: 110px;
      height: ${theme.buttonHeight.xsmall || '32px'};
      border-radius: ${theme.buttonBorderRadius.xsmall || '4px'};
    `}

  ${({ size, theme }) =>
    size === 'small' &&
    `
      font-size: ${theme.buttonFontSize.small};
      line-height: 20px;
      height: ${theme.buttonHeight.small || '44px'};
      min-width: 150px;
      border-radius: ${theme.buttonBorderRadius.small || '4px'};

      ${media.tabletPortrait} {
        font-size: 14px;
      }
    `}

  ${({ size, theme }) =>
    size === 'smallmedium' &&
    `
      font-size: ${theme.buttonFontSize.medium};
      line-height: 20px;
      min-width: 190px;
      height: ${theme.buttonHeight.smallmedium || '44px'};
      border-radius: ${theme.buttonBorderRadius.medium || '4px'};

      ${media.tabletPortrait} {
        font-size: 14px;
      }
    `}
  
  ${({ size, theme }) =>
    size === 'medium' &&
    `
      font-size: ${theme.buttonFontSize.medium};
      line-height: 20px;
      min-width: 190px;
      height: ${theme.buttonHeight.medium || '44px'};
      border-radius: ${theme.buttonBorderRadius.medium || '4px'};

      ${media.tabletPortrait} {
        font-size: 14px;
      }
    `}

  ${({ size, theme }) =>
    size === 'large' &&
    `
      font-size: ${theme.buttonFontSize.large};
      line-height: 20px;
      min-width: 300px;
      height: ${theme.buttonHeight.large || '54px'};
      border-radius: ${theme.buttonBorderRadius.large || '4px'};

      ${media.tabletPortrait} {
        font-size: 14px;
      }
    `}

  width: ${({ width }) => width || 'auto'};

  ${({ backGroundColor, variant }) =>
    backGroundColor
      ? `
        background-color: ${variant === 'secondary' ? 'transparent' : backGroundColor + 'EE'};

        &:hover,
        &:focus {
          background-color: ${variant === 'secondary' ? 'transparent' : backGroundColor};
          box-shadow: 0 0 3px ${backGroundColor};
        }
      `
      : ''}
`;

const StyledSpan = styled.span<{ size: 'xsmall' | 'small' | 'smallmedium' | 'medium' | 'large' }>`
  ${({ size, theme }) => `
    font-size: ${theme.buttonFontSize[size]};
    
    &:before {
      content: '';
      margin-bottom: ${theme.buttonBaselineAdjustment[size]};
      display: table;
    }
  `}
`;

const ButtonWithChildren: React.FC<ButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  as,
  backGroundColor,
  children,
  href,
  size = 'medium',
  variant = 'primary',
  width,
  ...buttonProps
}) => (
  <Button
    as={as}
    href={href}
    backgroundColor={backGroundColor}
    size={size}
    variant={variant}
    width={width}
    {...(buttonProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
  >
    {children && <StyledSpan size={size}>{children}</StyledSpan>}
  </Button>
);

export default ButtonWithChildren;
